import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    setToken(null);
    setUser(null);
  }, []);

  // Check token expiry
  useEffect(() => {
    const checkExpiry = () => {
      const expiry = localStorage.getItem('tokenExpiry');
      if (expiry && new Date(expiry) < new Date()) {
        logout();
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [logout]);

  // Fetch user on mount if token exists
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    
    const { token: newToken, user: userData } = response.data;
    
    // Set expiry to 24 hours from now
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + 24);
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('tokenExpiry', expiry.toISOString());
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password
    });
    
    const { token: newToken, user: userData } = response.data;
    
    // Set expiry to 24 hours from now
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + 24);
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('tokenExpiry', expiry.toISOString());
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const isMainUser = user?.role === 'MAIN_USER';

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isMainUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
